import { clsx } from "clsx";
import React from "react";

/**
 * Properties for {@link ToggleButton}.
 */
type ToggleButtonProps = {
    /** Whether the toggle is currently on */
    active: boolean;

    /** Called when the button is clicked, to flip the toggle */
    onClick: () => void;

    /** Additional classname to set */
    className?: string;

    children: React.ReactNode;
};

/**
 * A button that toggles some boolean state on and off.
 *
 * Styled to match the height, radius, and border of the surrounding form
 * controls (`<Input>`, `<Listbox>`) rather than the heavier `<Button>`
 * call-to-action look, so it sits naturally in a filter/search toolbar.
 */
export function ToggleButton(props: ToggleButtonProps) {
    const { active, onClick, className, children } = props;

    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={clsx(
                className,
                // Basic layout — matches Input/Listbox control sizing
                "inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)]",
                // Typography
                "text-base/6 font-normal sm:text-sm/6",
                // Icon
                "*:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 sm:*:data-[slot=icon]:size-4",
                // Focus ring
                "focus:outline-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--color-brand-500)",
                active
                    ? "border-(--color-brand-500)/20 bg-(--color-brand-500)/10 text-(--color-brand-700) hover:border-(--color-brand-500)/40 dark:border-(--color-brand-400)/40 dark:bg-(--color-brand-900)/40 dark:text-(--color-brand-300) dark:hover:border-(--color-brand-400)/40"
                    : "border-zinc-950/10 bg-transparent text-zinc-700 hover:border-zinc-950/20 dark:border-white/10 dark:text-zinc-300 dark:hover:border-white/20",
            )}
        >
            {children}
        </button>
    );
}
